import Page from '../../../components/PageWrapper';
import { useMemo, useState } from 'react';
import GTable from '../../../components/elements/GTable';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { GTableActionColumn, ITablePaginationData, ITableRowAction } from '../../../components/elements/GTable/GTable';
import useModal from '../../../hooks/modalHook';
import { capitalizeSentence, getFormattedDate, getPhoneNumberFromShortCodeFormat } from '../../../utils/miscFunctions';
import useRoute from '../../../hooks/routesHook';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { UPDATE_INSURERS } from '../../../states/slices/resourceSlice';
import useResource from '../../../hooks/resourcesHook';
import { IInitResources } from '../../../resources/interfaces';
import { IVerifiedVehicle } from '../../../models/verifiedVehicle/IVerifiedVehicle';
import { VerifiedVehicle } from '../../../models/verifiedVehicle/VerifiedVehicle';
import clsx from 'clsx';
import { getAllVehicleVerifications } from '../../../api/requests';
import Button from '../../../components/form-control/Button';
import { Building, Camera, Edit, Eye, TruckElectric } from 'lucide-react';
import usePartner from '../../../hooks/partnerHook';
import { COMPANY_TYPES } from '../../../resources/enums';
import useUserAccount from '../../../hooks/userAccountHook';
import useVehicleVerification from '../../../hooks/vehicleInspectionHook';
import OverviewPanel from './OverviewPanel';
import DashboardDatePickers, { SelectedDateRange } from '../../../components/dashboard/date-range-selector/datePicker';
import { Company } from '../../../models/company/Company';
import SelectDropdown from '../../../components/form-control/SelectDropdown';

const VehicleVerification = (): JSX.Element => {
    const page = 'vehicle inspection';
    const { user } = useUserAccount();
    const dispatch = useDispatch();
    const [allVehicleVerifications, setAllVehicleVerifications] = useState<VerifiedVehicle[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
    const [loading, setLoading] = useState<boolean>(true);
    const [vehiclesPaginationData, setVehiclesPaginationData] = useState<ITablePaginationData>();
    const [showPerPage, setShowPerPage] = useState<number>(100);
    const { ModalComponent, updateModalStates, closeModal } = useModal();
    const navigate = useNavigate();
    const { getDashboardBase } = useRoute();
    const { getInitResources } = useResource();
    const { handleAddVehicleVerification, fetchVehicleVerifiactionMetrics, VeificationModal, showFleetVehicleVerificationUploadModal, hadnleEditVehicleVerification } = useVehicleVerification();
    const invalidateQueries = useQueryClient();
    const { fetchAllCompaniesByTypes } = usePartner();
    const [selectedDateRange, setSelectedDateRange] = useState<any>({
        from: null,
        to: null
    });

    const { data: companies } = useQuery({
        queryKey: ['companies'],
        queryFn: () => fetchAllCompaniesByTypes([COMPANY_TYPES.INSURER]),
        enabled: !!user.isOwner()
    });

    const companyId = !user?.isInternalAdmin() ? user?.company?.id : companies?.[0]?.id ?? ''
    const [selectedCompany, setSelectedCompany] = useState<any>(undefined);


    const { data: vehicleVerificationMetrics, isLoading: vehicleVerificationMetricsLoading } = useQuery({
        queryKey: ['fetchVehicleVerificationMetrics', selectedCompany, selectedDateRange, companyId],
        queryFn: () => fetchVehicleVerifiactionMetrics(selectedCompany ? selectedCompany : companyId, selectedDateRange?.from, selectedDateRange?.to),
        enabled: !!selectedCompany || !!companyId
    })

    const initializeResources = async () => {
        try {
            const result: IInitResources = await getInitResources();

            if (result) {
                dispatch(UPDATE_INSURERS(result?.insurers));
            }
        } catch (error) {
            console.log(error)
        }
    }

    useQuery({
        queryKey: ['initializeResources'],
        queryFn: () => initializeResources(),
    });

    const { data: vehicleVerifications, isLoading: vehicleVerificationsLoading } = useQuery({
        queryKey: ['fetchAllVehicleVerifications', currentPage, showPerPage, selectedCompany, selectedDateRange],
        queryFn: () => fetchAllVehicleVerifications()
    });

    const fetchAllVehicleVerifications = async () => {
        try {
            const response: any = await getAllVehicleVerifications(currentPage, showPerPage, user.isInternalAdmin() ? selectedCompany : '', selectedDateRange.from, selectedDateRange.to);

            if (response) {
                const _vehicles = response?.content.map((_vehicle: IVerifiedVehicle) => new VerifiedVehicle(_vehicle));
                setAllVehicleVerifications(_vehicles);

                const from = response.pageable?.offset + 1;
                const to =
                    from + response.size > response.totalElements
                        ? response.totalElements
                        : from + response.size - 1;

                setVehiclesPaginationData({
                    ...vehiclesPaginationData,
                    per_page: response.size ?? 0,
                    total: response.totalElements ?? 0,
                    totalPages: response.totalPages ?? 0,
                    from,
                    to
                });


            }
        } catch (error) {
        } finally {
            setLoading(false);
        }


    };

    const columns = useMemo(
        () => {
            if (user.isInternalAdmin()) {
                return [
                    {
                        Header: 'M',
                        columns: [
                            {
                                Header: 'Application No.',
                                accessor: 'applicationNumber',
                                Cell: (row: any) => {
                                    return (
                                        <Link
                                            to={`${getDashboardBase()}/vehicle-verification/${row?.cell.value}`}
                                            className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
                                            {row.cell.value ?? '-'}
                                        </Link>
                                    );
                                }
                            },
                            {
                                Header: 'Insurance Company',
                                accessor: 'insurerName',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? row.cell.value : '-'}</span>
                                }
                            },
                            {
                                Header: 'Applicant',
                                accessor: 'customerName',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? `${row.cell.value}` : '-'}</span>
                                }
                            },
                            {
                                Header: 'Phone',
                                accessor: 'customerPhone',
                                Cell: (row: any) => {
                                    return (
                                        <span>
                                            {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                                        </span>
                                    );
                                }
                            },
                            {
                                Header: 'No. of Vehicles',
                                accessor: 'numberOfVehicles',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ?? '-'}</span>;
                                }
                            },
                            {
                                Header: 'Initiated By',
                                accessor: 'staff',
                                Cell: (row: any) => {
                                    return <span> {row.row.original.staff?.fullName ?? '-'} </span>
                                }
                            },
                            {
                                Header: 'Created On',
                                accessor: 'createdOn',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>;
                                }
                            },
                            {
                                Header: 'Updated On',
                                accessor: 'updatedOn',
                                Cell: (row: any) => {
                                    return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>;
                                }
                            },

                            {
                                Header: 'Action',
                                id: 'Action',
                                Cell: (row: any) => {
                                    return (
                                        <GTableActionColumn actions={getTableRowActions(row.row.original)} />
                                    );
                                }
                            }
                        ]
                    }
                ]
            }

            return [
                {
                    Header: 'M',
                    columns: [
                        {
                            Header: 'Application No.',
                            accessor: 'applicationNumber',
                            Cell: (row: any) => {
                                return (
                                    <Link
                                        to={`${getDashboardBase()}/vehicle-verification/${row?.cell.value}`}
                                        className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
                                        {row.cell.value ?? '-'}
                                    </Link>
                                );
                            }
                        },
                        {
                            Header: 'Applicant',
                            accessor: 'customer',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? (row.cell.value?.firstName + ' ' + row.cell.value?.lastName) : '-'}</span>
                            }
                        },
                        {
                            Header: 'Phone',
                            accessor: 'customer.phone',
                            Cell: (row: any) => {
                                return (
                                    <span>
                                        {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                                    </span>
                                );
                            }
                        },

                        {
                            Header: 'No. of Vehicles',
                            accessor: 'numberOfVehicles',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ?? '-'}</span>;
                            }
                        },
                        {
                            Header: 'Type',
                            accessor: 'vehicles',
                            Cell: (row: any) => {
                                const isFleet = row.cell.value?.length > 1;
                                return (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isFleet
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-green-100 text-green-800'
                                        }`}>
                                        {isFleet ? 'FLEET' : 'SINGLE'}
                                    </span>
                                );
                            }
                        },
                        {
                            Header: 'Status',
                            accessor: 'active',
                            Cell: (row: any) => {
                                return (
                                    <div className="w-full flex text-white">
                                        <span
                                            className={clsx(
                                                'w-[30px] h-[30px] rounded-full flex items-center justify-center text-[0px] ',
                                                { ['bg-status-active']: row.row.original?.verified === true },
                                                { ['bg-status-ended']: row.row.original?.verified === false }
                                            )}>
                                            {row.row.original?.verified}
                                        </span>
                                    </div>
                                );
                            }
                        },
                        {
                            Header: 'Initiated By',
                            accessor: 'staff',
                            Cell: (row: any) => {
                                return <span> {row.row.original.staff?.fullName ?? '-'} </span>
                            }
                        },
                        {
                            Header: 'Created On',
                            accessor: 'createdOn',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Updated On',
                            accessor: 'updatedOn',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>;
                            }
                        },

                        {
                            Header: 'Action',
                            id: 'Action',
                            Cell: (row: any) => {
                                return (
                                    <GTableActionColumn actions={getTableRowActions(row.row.original)} />
                                );
                            }
                        }
                    ]
                }
            ]
        },
        []
    );

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setSearchParams({ page: String(page) });
    };

    const getTableRowActions = (vehicleInfo: any) => {
        const tableActions: ITableRowAction[] = [
            {
                label: 'View',
                icon: <Eye className="h-4 w-4" />,
                callback: () => navigate(`${getDashboardBase()}/vehicle-verification/${vehicleInfo?.id}`)
            },
            {
                label: 'Edit',
                icon: <Edit className="h-4 w-4" />,
                callback: () => hadnleEditVehicleVerification(vehicleInfo)
            },
        ];

        return tableActions;
    }


    return (
        <Page title={`${capitalizeSentence(page)}`}>
            <div className="bg-white dark:bg-darkBg w-full p-5 text-sm min-h-full rounded-lg shadow-md dark:shadow-darkBg-shadow">
                <div className="flex justify-between items-center">
                    <div className="">
                        <div className="inline-flex items-center gap-2 mb-2 dark:text-darkBg-mainText">
                            <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                            <h2 className="font-semibold text-3xl">{capitalizeSentence(page)}s</h2>
                        </div>

                        <p className="text-sm text-slate-400">
                            View a list of all {page}s and their details{' '}
                        </p>
                    </div>

                    <div className="flex justify-end items-center gap-4">
                        {(user?.isInternalAdmin() && companies && companies?.length > 0) && (
                            <SelectDropdown
                                list={companies?.map((company: Company) => ({
                                    name: company.name,
                                    value: company.id,
                                }))}
                                placeholder='Select Company'
                                listSelectedValue={selectedCompany}
                                prepend={<Building size={16} className="text-gray-500 mr-2" />}
                                onChange={(value: string) => setSelectedCompany(Number(value))}
                            />
                        )}

                        <DashboardDatePickers
                            onSubmit={(data: SelectedDateRange) => {
                                setSelectedDateRange(data);
                            }}
                        />

                    </div>
                </div>

                <div className="mt-6">
                    <OverviewPanel
                        data={{
                            completedVerifications: vehicleVerificationMetrics?.completedVerifications,
                            completionRate: vehicleVerificationMetrics?.completionRate,
                            pendingVerifications: vehicleVerificationMetrics?.pendingVerifications,
                            coverTypeDistribution: vehicleVerificationMetrics?.coverTypeDistribution,
                            totalVerifications: vehicleVerificationMetrics?.totalVerifications,
                        }}
                        setSelectedDateRange={(data: SelectedDateRange) => setSelectedDateRange(data)}
                    />
                </div>


                <div className="overflow-x-auto">
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            bgColor="primary"
                            label={`Single Inspection`}
                            icon={<Camera className="size-5 mr-1" />}
                            onClick={handleAddVehicleVerification}
                        />
                        <Button
                            bgColor="primary"
                            label={'Fleet Inspection'}
                            icon={<TruckElectric className='h-5 w-5' />}
                            onClick={() => navigate(`${getDashboardBase()}/vehicle-verification/create-fleet`)}
                        />
                    </div>
                    <GTable
                        columns={columns}
                        data={allVehicleVerifications}
                        loading={vehicleVerificationsLoading}
                        classes="h-fit"
                        showSearch={false}
                        searchPlaceholder={'Search by name or phone number...'}
                        tableType={'vehicle info'}
                        paginationData={vehiclesPaginationData}
                        onPageChange={handlePageChange}
                        showPerPage={showPerPage}
                        stickyHeader
                        onShowPerPageChange={(page: number) => setShowPerPage(page)}
                    />
                </div>
            </div>
            <ModalComponent />
            <VeificationModal />
        </Page>
    );
};

export default VehicleVerification;
